import React, { useState } from 'react';
import { Beaker, Factory, Package } from 'lucide-react';
import { chemicalProducts, getWhatsAppLink } from '../data/products';

// Product Icon Component
const ProductIcon = ({ type, size = 32, className = '' }) => {
  if (type === 'bottle') {
    return (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={className}
      >
        <path d="M7 3h10v2H7z"/>
        <path d="M9 5v2c0 1-1 2-1 2v10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9s-1-1-1-2V5"/>
      </svg>
    );
  }
  
  if (type === 'spray') {
    return (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={className}
      >
        <path d="M3 3v18h7v-6h4v6h7V3z"/>
        <path d="M16 3v2"/>
        <path d="M19 2v4"/>
        <path d="M22 3v2"/>
        <circle cx="7" cy="12" r="1"/>
        <circle cx="11" cy="9" r="1"/>
      </svg>
    );
  }
  
  // Default box/container icon
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );
};

export const ChemicalDivision = () => {
  const [activeTab, setActiveTab] = useState('cleaning');

  return (
    <section
      id="chemicals"
      className="py-24 md:py-32 px-6 md:px-12 bg-navy relative overflow-hidden"
      data-testid="chemical-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-50" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 rounded-full mb-6">
            <Beaker size={20} className="text-gold" />
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">Chemical Division</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Premium <span className="text-gold-gradient">Chemical Solutions</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Quality industrial chemicals and cleaning solutions for wholesale distribution.
          </p>
          <div className="section-divider mt-8" />
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setActiveTab('cleaning')}
            className={`px-8 py-3 rounded-sm font-semibold transition-all duration-300 uppercase tracking-wider text-sm ${
              activeTab === 'cleaning'
                ? 'bg-gradient-to-r from-gold-metallic to-gold-dark text-navy shadow-lg shadow-gold/30'
                : 'bg-transparent text-silver border border-silver/30 hover:border-gold/50 hover:text-gold'
            }`}
            data-testid="tab-cleaning"
          >
            <Package size={18} className="inline mr-2" />
            Cleaning Solutions
          </button>
          <button
            onClick={() => setActiveTab('industrial')}
            className={`px-8 py-3 rounded-sm font-semibold transition-all duration-300 uppercase tracking-wider text-sm ${
              activeTab === 'industrial'
                ? 'bg-gradient-to-r from-gold-metallic to-gold-dark text-navy shadow-lg shadow-gold/30'
                : 'bg-transparent text-silver border border-silver/30 hover:border-gold/50 hover:text-gold'
            }`}
            data-testid="tab-industrial"
          >
            <Factory size={18} className="inline mr-2" />
            Industrial Chemicals
          </button>
        </div>

        {/* Cleaning Solutions */}
        {activeTab === 'cleaning' && (
          <div>
            {/* Section Header */}
            <div className="text-center mb-12 animate-fade-in">
              <h3 className="text-2xl md:text-3xl font-heading font-semibold text-gold-gradient mb-4">
                Premium Cleaning Solutions
              </h3>
              <p className="text-gray-300">Professional-grade cleaning products for home and commercial use</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              {chemicalProducts.cleaning.map((product, index) => (
                <div
                  key={product.id}
                  className="product-card group opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                  data-testid={`product-${product.id}`}
                >
                  <div className="relative h-full overflow-hidden bg-navy-light border border-gold/10 hover:border-gold/40 transition-all duration-500 p-6 rounded-sm">
                    {/* Product Icon/Placeholder */}
                    <div className="mb-6 flex items-center justify-between">
                      <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                        <ProductIcon type={product.icon || 'box'} size={32} className="text-gold" />
                      </div>
                      <span className="px-3 py-1 bg-gold/20 border border-gold/30 rounded-full text-gold text-xs font-semibold uppercase tracking-wider">
                        {product.brand}
                      </span>
                    </div>

                    {/* Product Name */}
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3 group-hover:text-gold transition-colors">
                      {product.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">{product.description}</p>

                    {/* Features */}
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2">Key Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-white/5 text-gray-300 text-xs rounded-sm border border-white/10"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Sizes */}
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gold uppercase tracking-wider mb-2">Available Sizes</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.sizes.map((size, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gold/10 text-gold text-xs rounded-sm border border-gold/30 font-semibold"
                          >
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <a
                      href={getWhatsAppLink(product.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center px-4 py-3 bg-gradient-to-r from-gold-metallic to-gold-dark hover:from-gold-dark hover:to-gold-metallic text-navy font-bold text-sm rounded-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-gold/50 uppercase tracking-wider"
                      data-testid={`enquire-btn-${product.id}`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        Enquire for Wholesale
                      </span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Industrial Chemicals */}
        {activeTab === 'industrial' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in">
            {chemicalProducts.industrial.map((product, index) => (
              <div
                key={product.id}
                className="product-card group opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`product-${product.id}`}
              >
                <div className="relative h-full overflow-hidden bg-navy-light border border-gold/10 hover:border-gold/40 transition-all duration-500 p-8 rounded-sm">
                  {/* Product Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3 group-hover:text-gold transition-colors">
                        {product.name}
                      </h3>
                      <span className="inline-block px-4 py-1 bg-gold/20 border border-gold/30 rounded-full text-gold text-sm font-semibold uppercase tracking-wider">
                        {product.grade}
                      </span>
                    </div>
                    <Factory size={48} className="text-gold/20 group-hover:text-gold/40 transition-colors" />
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 leading-relaxed mb-6">{product.description}</p>

                  {/* Specifications */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Specifications</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.specifications.map((spec, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white/5 text-gray-300 text-sm rounded-sm border border-white/10"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Applications */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Applications</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {product.applications.map((app, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                          <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
                          {app}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <a
                    href={getWhatsAppLink(product.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-gold-metallic to-gold-dark hover:from-gold-dark hover:to-gold-metallic text-navy font-bold rounded-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-gold/50 uppercase tracking-wider"
                    data-testid={`enquire-btn-${product.id}`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Enquire for Wholesale
                    </span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ISO Certification Notice */}
        <div className="mt-16 glass-panel p-8 rounded-lg border-gold-glow text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Package size={32} className="text-gold" />
            <h3 className="text-2xl font-heading font-semibold text-gold-gradient">Quality Assured</h3>
          </div>
          <p className="text-gray-300 text-lg leading-relaxed">
            <span className="text-gold font-semibold">Chemical Division:</span> Manufactured by ISO certified partners,
            <br className="hidden md:block" /> Marketed by Agatsya International
          </p>
        </div>
      </div>
    </section>
  );
};
